import * as Api from '@ViewModels';
import { ActionItemViewModel } from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css, StyleSheet } from 'aphrodite';
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import * as React from 'react';
import { toast } from 'sonner';
import { useAccountContext } from '../../../admin/components/blogs/AccountContext';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { useSingleEmailComposer, useUserSession } from '../../../models/hooks/appStateHooks';
import { actionItemQueryKeys } from '../../../queries/ActionItem/actionItemQueryKeys';
import { useActionItemMutations } from '../../../queries/ActionItem/useActionItemMutations';
import { useOpenActionItemsQuery } from '../../../queries/ActionItem/useOpenActionItemsQuery';
import { useLoadAllUsers } from '../../../queries/User/useLoadAllUsers';
import { Button } from '../../components-v2/Button';
import {
	Dialog,
	DialogClose,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../components-v2/Dialog';
import { THEME } from '../../components-v2/Theme/theme';
import { ActionItemDetailsFlyout } from '../../components/actionItems/ActionItemDetailsFlyout';
import { ActionItemsCalendarView } from '../../components/actionItems/ActionItemsCalendarView';
import { invalidateAllCalendarActionItems } from '../../components/calendar/hooks/useCalendarActionItems';
import { ActionItemGroups } from './components/ActionItemGroups';
import { DayHeader } from './components/DayHeader';
import { ACTION_ITEMS_VIEWS, ActionItemsHeader } from './components/ActionItemsHeader';
import { BulkActionBar } from './components/BulkActionBar';
import { CompletedList } from './components/CompletedList';
import { FocusMode } from './components/FocusMode';
import { IQuickAddControl, QuickAddBar } from './components/QuickAddBar';
import { useActionItemSelection } from './hooks/useActionItemSelection';
import { useActionItemsKeyboardShortcuts } from './hooks/useActionItemsKeyboardShortcuts';
import { getActionItemTitle } from './utils/actionItemText';
import { formatDueLabel } from './utils/formatDue';
import {
	ActionItemBucket,
	BUCKET_LABELS,
	BUCKET_ORDER,
	bucketQuickAddPhrase,
	dueDateForBucket,
	groupActionItems,
	IDueDateValue,
} from './utils/buckets';
import { selectionAfterBulk } from './utils/selection';

const REGROUP_INTERVAL_MS = 60 * 1000;

export const ActionItemsPage: React.FC = () => {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const singleEmailComposer = useSingleEmailComposer();
	const { impersonationContext } = useAccountContext();
	const currentUserId = impersonationContext?.user?.id ?? userSession.user?.id;

	const [scope, setScope] = useQueryState('scope', parseAsString.withDefault('mine'));
	const [search, setSearch] = useQueryState('q', parseAsString.withDefault(''));
	const [view, setView] = useQueryState('view', parseAsStringLiteral(ACTION_ITEMS_VIEWS).withDefault('list'));
	const [contactId, setContactId] = useQueryState('contact', parseAsString);
	const debouncedSearch = useDebounceValue(search, 300);
	const assignedTo = scope === 'everyone' ? undefined : scope === 'mine' ? currentUserId : scope;

	const { users } = useLoadAllUsers({ impersonationContext });
	const openQuery = useOpenActionItemsQuery({
		assignedTo,
		search: debouncedSearch,
		contactId,
		impersonationContext,
		enabled: view !== 'completed',
	});
	const mutations = useActionItemMutations({ impersonationContext });

	const [now, setNow] = React.useState(() => new Date());
	React.useEffect(() => {
		const timer = window.setInterval(() => setNow(new Date()), REGROUP_INTERVAL_MS);
		return () => window.clearInterval(timer);
	}, []);

	const items = React.useMemo(() => openQuery.data?.items ?? [], [openQuery.data]);
	const groups = React.useMemo(() => groupActionItems(items, now), [items, now]);
	const orderedIds = React.useMemo(() => BUCKET_ORDER.flatMap(bucket => groups[bucket].map(item => item.id)), [groups]);
	const itemsById = React.useMemo(() => new Map(items.map(item => [item.id, item] as const)), [items]);
	const selection = useActionItemSelection(orderedIds);

	const quickAddRef = React.useRef<HTMLInputElement>(null);
	const quickAddControlRef = React.useRef<IQuickAddControl | null>(null);
	const searchRef = React.useRef<HTMLInputElement>(null);
	const [focusQueueIds, setFocusQueueIds] = React.useState<string[] | null>(null);
	const [focusSuspended, setFocusSuspended] = React.useState(false);
	const [flyoutId, setFlyoutId] = React.useState<string | null>(null);
	const [flyoutSnapshot, setFlyoutSnapshot] = React.useState<Api.IActionItem | null>(null);
	const [flyoutOpen, setFlyoutOpen] = React.useState(false);
	const [flyoutStartInEdit, setFlyoutStartInEdit] = React.useState(false);
	const [pendingDelete, setPendingDelete] = React.useState<Api.IActionItem | null>(null);

	useActionItemsKeyboardShortcuts({
		enabled: view === 'list' && !focusQueueIds && !flyoutOpen && !pendingDelete,
		bindings: {
			n: () => quickAddRef.current?.focus(),
			'/': () => searchRef.current?.focus(),
		},
	});

	const invalidateEverything = React.useCallback(() => {
		queryClient.invalidateQueries({ queryKey: actionItemQueryKeys.all });
		invalidateAllCalendarActionItems();
	}, [queryClient]);

	const onComplete = React.useCallback(
		(item: Api.IActionItem) => {
			mutations.toggleComplete.mutate({ item, isCompleted: true });
			toast.success(`Completed “${getActionItemTitle(item)}”`, {
				action: {
					label: 'Undo',
					onClick: () => mutations.toggleComplete.mutate({ item, isCompleted: false }),
				},
			});
		},
		[mutations.toggleComplete]
	);
	const onReschedule = React.useCallback(
		(item: Api.IActionItem, value: IDueDateValue) => mutations.update.mutate({ item, patch: value }),
		[mutations.update]
	);
	const onSetPriority = React.useCallback(
		(item: Api.IActionItem, priority: Api.ActionItemPriority) => mutations.update.mutate({ item, patch: { priority } }),
		[mutations.update]
	);
	const onReassign = React.useCallback(
		(item: Api.IActionItem, user: Api.IUser) => mutations.update.mutate({ item, patch: { assignee: user } }),
		[mutations.update]
	);
	const openFlyout = React.useCallback((item: Api.IActionItem, startInEdit: boolean) => {
		setFlyoutId(item.id);
		setFlyoutSnapshot(item);
		setFlyoutStartInEdit(startInEdit);
		setFlyoutOpen(true);
	}, []);
	const onOpen = React.useCallback((item: Api.IActionItem) => openFlyout(item, false), [openFlyout]);
	const onEdit = React.useCallback((item: Api.IActionItem) => openFlyout(item, true), [openFlyout]);
	const onDelete = React.useCallback((item: Api.IActionItem) => setPendingDelete(item), []);
	const onFocusFrom = React.useCallback(
		(item: Api.IActionItem) => {
			const start = orderedIds.indexOf(item.id);
			setFocusQueueIds(start >= 0 ? orderedIds.slice(start) : [item.id]);
		},
		[orderedIds]
	);
	const onSendEmail = React.useCallback(
		(item: Api.IActionItem) => {
			// The composer opens beneath a modal dialog, so Focus steps aside until the email is done.
			setFocusSuspended(true);
			singleEmailComposer.showForActionItem(new ActionItemViewModel(userSession, item), {
				onFinish: didSend => {
					setFocusSuspended(false);
					if (didSend) {
						setFocusQueueIds(ids => (ids ? ids.filter(id => id !== item.id) : ids));
						invalidateEverything();
					}
				},
				onDismiss: () => setFocusSuspended(false),
			});
		},
		[singleEmailComposer, userSession, invalidateEverything]
	);
	const onDropToBucket = React.useCallback(
		(id: string, bucket: ActionItemBucket) => {
			const item = itemsById.get(id);
			if (!item) {
				return;
			}
			const value = dueDateForBucket(
				bucket,
				{ dueDate: item.dueDate ?? null, hasDueTime: !!item.hasDueTime },
				new Date()
			);
			if (!value || (value.dueDate === (item.dueDate ?? null) && value.hasDueTime === !!item.hasDueTime)) {
				return;
			}
			mutations.update.mutate({ item, patch: value });
		},
		[itemsById, mutations.update]
	);

	const runBulk = (request: Omit<Api.IBulkActionItemRequest, 'ids'> & { assignee?: Api.IUser }) => {
		const ids = [...selection.selectedIds];
		if (ids.length === 0) {
			return;
		}
		mutations.bulk.mutate(
			{ ...request, ids },
			{
				onSuccess: result => selection.replace(selectionAfterBulk(new Set(ids), result)),
			}
		);
	};

	const onAddToBucket = React.useCallback((bucket: ActionItemBucket) => {
		const phrase = bucketQuickAddPhrase(bucket, new Date());
		quickAddControlRef.current?.appendPhrase(phrase ?? '');
	}, []);

	const focusPlan = React.useMemo(() => {
		if (selection.hasSelection) {
			return { ids: orderedIds.filter(id => selection.selectedIds.has(id)), scope: 'selected' };
		}
		const dueNow = [...groups.overdue, ...groups.today].map(item => item.id);
		if (dueNow.length > 0) {
			return { ids: dueNow, scope: 'today' };
		}
		const nextBucket = BUCKET_ORDER.find(bucket => groups[bucket].length > 0);
		return nextBucket
			? { ids: groups[nextBucket].map(item => item.id), scope: BUCKET_LABELS[nextBucket].toLowerCase() }
			: { ids: [], scope: 'today' };
	}, [selection.hasSelection, selection.selectedIds, orderedIds, groups]);
	const startFocus = () => {
		if (focusPlan.ids.length > 0) {
			setFocusQueueIds(focusPlan.ids);
		}
	};

	const flyoutItem = flyoutId ? (itemsById.get(flyoutId) ?? flyoutSnapshot) : null;

	return (
		<div className={css(styleSheet.page)} data-testid='action-items-page'>
			<ActionItemsHeader
				scope={scope}
				onScopeChange={value => {
					selection.clear();
					setScope(value);
				}}
				users={users}
				currentUserId={currentUserId}
				search={search}
				onSearchChange={setSearch}
				searchInputRef={searchRef}
				contactId={contactId}
				onContactChange={contact => {
					selection.clear();
					setContactId(contact?.id ?? null);
				}}
				impersonationContext={impersonationContext}
				view={view}
				onViewChange={setView}
			/>
			{view === 'list' ? (
				<>
					<DayHeader
						now={now}
						overdueCount={groups.overdue.length}
						todayCount={groups.today.length}
						tomorrowCount={groups.tomorrow.length}
						focusCount={focusPlan.ids.length}
						focusScope={focusPlan.scope}
						isLoading={openQuery.isPending}
						onStartFocus={startFocus}
					/>
					<QuickAddBar
						inputRef={quickAddRef}
						controlRef={quickAddControlRef}
						impersonationContext={impersonationContext}
						onCreate={input =>
							mutations.create.mutate(input, {
								onSuccess: saved => {
									const due = saved.dueDate ? new Date(saved.dueDate) : null;
									toast.success(
										`Added “${getActionItemTitle(saved)}” ${due ? `for ${formatDueLabel(due, !!saved.hasDueTime)}` : 'with no due date'}`,
										{ action: { label: 'View', onClick: () => openFlyout(saved, false) } }
									);
								},
							})
						}
					/>
					<ActionItemGroups
						groups={groups}
						isLoading={openQuery.isPending}
						isRefreshing={openQuery.isFetching && !openQuery.isPending}
						hasMore={!!openQuery.data?.hasMore}
						users={users}
						selectedIds={selection.selectedIds}
						onToggleSelected={selection.toggle}
						onDropToBucket={onDropToBucket}
						onAddToBucket={onAddToBucket}
						onComplete={onComplete}
						onReschedule={onReschedule}
						onSetPriority={onSetPriority}
						onReassign={onReassign}
						onOpen={onOpen}
						onEdit={onEdit}
						onDelete={onDelete}
						onFocus={onFocusFrom}
					/>
					<BulkActionBar
						count={selection.selectedIds.size}
						users={users}
						onComplete={() => runBulk({ operation: 'Complete' })}
						onReschedule={value =>
							runBulk({ operation: 'Reschedule', dueDate: value.dueDate, hasDueTime: value.hasDueTime })
						}
						onReassign={user => runBulk({ operation: 'Reassign', assigneeId: user.id, assignee: user })}
						onSetPriority={priority => runBulk({ operation: 'SetPriority', priority })}
						onDelete={() => runBulk({ operation: 'Delete' })}
						onClear={selection.clear}
					/>
				</>
			) : null}
			{view === 'calendar' ? <ActionItemsCalendarView assignedToUserId={assignedTo} search={debouncedSearch} /> : null}
			{view === 'completed' ? (
				<CompletedList
					assignedTo={assignedTo}
					search={debouncedSearch}
					contactId={contactId}
					impersonationContext={impersonationContext}
					onUncomplete={item => mutations.toggleComplete.mutate({ item, isCompleted: false })}
					onOpen={onOpen}
				/>
			) : null}
			<FocusMode
				open={!!focusQueueIds && !focusSuspended}
				onClose={() => setFocusQueueIds(null)}
				queueIds={focusQueueIds ?? []}
				itemsById={itemsById}
				users={users}
				onComplete={onComplete}
				onReschedule={onReschedule}
				onSetPriority={onSetPriority}
				onReassign={onReassign}
				onSendEmail={onSendEmail}
			/>
			{flyoutItem ? (
				<ActionItemDetailsFlyout
					isOpen={flyoutOpen}
					setIsOpen={setFlyoutOpen}
					actionItem={flyoutItem}
					startInEdit={flyoutStartInEdit}
					onChanged={invalidateEverything}
				/>
			) : null}
			<Dialog open={!!pendingDelete} onOpenChange={open => !open && setPendingDelete(null)}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Delete action item?</DialogTitle>
						<DialogDescription>
							{pendingDelete ? `“${getActionItemTitle(pendingDelete)}” will be gone forever.` : ''}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<DialogClose
							render={props => (
								<Button variant='secondary' {...props}>
									Cancel
								</Button>
							)}
						/>
						<Button
							variant='destructive'
							onClick={() => {
								if (pendingDelete) {
									mutations.remove.mutate({ item: pendingDelete });
								}
								setPendingDelete(null);
							}}
						>
							Delete
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</div>
	);
};

const styleSheet = StyleSheet.create({
	page: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		flex: 1,
		minHeight: 0,
		minWidth: 0,
		paddingBottom: THEME.spacing.xl,
	},
});
